import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

import { planningAPI } from "../services/planningService";
import { employeesAPI } from "../services/api";
import AssignmentInfoAlert from "./schedule/AssignmentInfoAlert";
import AssignmentTable from "./schedule/AssignmentTable";
import AssignmentCreateDialog from "./schedule/AssignmentCreateDialog";
import AssignmentEditDialog from "./schedule/AssignmentEditDialog";
import AssignmentDeleteDialog from "./schedule/AssignmentDeleteDialog";

const emptyForm = {
  assignment_mode: "single",
  employee_id: "",
  employee_ids: [],
  assignment_type: "schedule",
  schedule_id: "",
  cycle_pattern_id: "",
  cycle_start_week: 1,
  start_date: new Date().toISOString().split("T")[0],
  end_date: "",
  reason: "",
  replace_existing: false,
};

const ScheduleAssignmentManager = () => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [cyclePatterns, setCyclePatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadAssignments(), loadEmployees(), loadSchedules(), loadCyclePatterns()]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const data = await planningAPI.assignments.getScheduleAssignments({ is_active: true });
      setAssignments(data.items || []);
    } catch (error) {
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await employeesAPI.getEmployees({ limit: 1000 });
      setEmployees(data.employees || []);
    } catch (error) {
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await planningAPI.schedules.getSchedules({ is_active: true, limit: 100 });
      setSchedules(data.items || []);
    } catch (error) {
    }
  };

  const loadCyclePatterns = async () => {
    try {
      const data = await planningAPI.cyclePatterns.getCyclePatterns({ is_active: true, limit: 100 });
      setCyclePatterns(data.items || []);
    } catch (error) {
    }
  };

  const resetForm = () => setFormData(emptyForm);

  const handleOpenCreateModal = () => { resetForm(); setShowCreateModal(true); };

  const handleOpenEditModal = (assignment) => {
    setSelectedAssignment(assignment);
    setFormData({
      employee_id: assignment.employee_id,
      assignment_type: assignment.cycle_pattern_id ? "cycle" : "schedule",
      schedule_id: assignment.schedule_id || "",
      cycle_pattern_id: assignment.cycle_pattern_id || "",
      cycle_start_week: assignment.cycle_start_week || 1,
      start_date: assignment.start_date,
      end_date: assignment.end_date || "",
      reason: "",
    });
    setShowEditModal(true);
  };

  const handleOpenDeleteDialog = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteDialog(true);
  };

  const handleCreateAssignment = async () => {
    if (formData.assignment_mode === "bulk") return handleBulkAssignment();
    try {
      if (!formData.employee_id) { toast.error(t("planning.legacy.assignments.errors.employeeRequired")); return; }
      if (!formData.start_date) { toast.error(t("planning.legacy.assignments.errors.startDateRequired")); return; }
      if (formData.assignment_type === "schedule" && !formData.schedule_id) { toast.error(t("planning.legacy.assignments.errors.scheduleRequired")); return; }
      if (formData.assignment_type === "cycle" && !formData.cycle_pattern_id) { toast.error(t("planning.legacy.assignments.errors.cycleRequired")); return; }

      const payload = {
        employee_id: parseInt(formData.employee_id),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        reason: formData.reason || t("planning.legacy.assignments.reasons.initial"),
      };
      if (formData.assignment_type === "schedule") {
        payload.schedule_id = parseInt(formData.schedule_id);
      } else {
        payload.cycle_pattern_id = parseInt(formData.cycle_pattern_id);
        payload.cycle_start_week = parseInt(formData.cycle_start_week);
      }

      await planningAPI.assignments.createScheduleAssignment(payload);
      toast.success(t("planning.legacy.assignments.created"));
      await loadAssignments();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      // error toast shown by global handler with backend detail
    }
  };

  const handleBulkAssignment = async () => {
    try {
      if (formData.employee_ids.length === 0) { toast.error(t("planning.legacy.assignments.errors.bulkEmployeeRequired")); return; }
      if (!formData.start_date) { toast.error(t("planning.legacy.assignments.errors.startDateRequired")); return; }
      if (formData.assignment_type === "schedule" && !formData.schedule_id) { toast.error(t("planning.legacy.assignments.errors.scheduleRequired")); return; }
      if (formData.assignment_type === "cycle" && !formData.cycle_pattern_id) { toast.error(t("planning.legacy.assignments.errors.cycleRequired")); return; }

      const payload = {
        employee_ids: formData.employee_ids.map((id) => parseInt(id)),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        reason: formData.reason || t("planning.legacy.assignments.reasons.bulk"),
        replace_existing: formData.replace_existing,
      };
      if (formData.assignment_type === "schedule") {
        payload.schedule_id = parseInt(formData.schedule_id);
      } else {
        payload.cycle_pattern_id = parseInt(formData.cycle_pattern_id);
        payload.cycle_start_week = parseInt(formData.cycle_start_week);
      }

      const response = await planningAPI.assignments.bulkCreateScheduleAssignments(payload);
      toast.success(t("planning.legacy.assignments.bulkCreated", {
        created: response.total_created,
        requested: response.total_requested,
        skipped: response.total_skipped,
        skippedText: response.total_skipped > 0 ? t("planning.legacy.assignments.bulkSkipped", { count: response.total_skipped }) : "",
      }));
      await loadAssignments();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      toast.error(t("planning.legacy.assignments.errors.bulkAssign"));
    }
  };

  const handleUpdateAssignment = async () => {
    try {
      if (!formData.start_date) { toast.error(t("planning.legacy.assignments.errors.startDateRequired")); return; }
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        reason: formData.reason || t("planning.legacy.assignments.reasons.update"),
      };
      if (formData.assignment_type === "schedule" && formData.schedule_id) {
        payload.schedule_id = parseInt(formData.schedule_id);
      }
      if (formData.assignment_type === "cycle" && formData.cycle_pattern_id) {
        payload.cycle_pattern_id = parseInt(formData.cycle_pattern_id);
        payload.cycle_start_week = parseInt(formData.cycle_start_week);
      }
      await planningAPI.assignments.updateScheduleAssignment(selectedAssignment.id, payload);
      await loadAssignments();
      setShowEditModal(false);
      setSelectedAssignment(null);
      resetForm();
    } catch (error) {
    }
  };

  const handleDeleteAssignment = async () => {
    try {
      await planningAPI.assignments.deleteScheduleAssignment(selectedAssignment.id);
      await loadAssignments();
      setShowDeleteDialog(false);
      setSelectedAssignment(null);
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-muted-foreground">{t("planning.legacy.assignments.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.legacy.assignments.title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("planning.legacy.assignments.subtitle")}
          </p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <UserPlus className="h-4 w-4 mr-2" /> {t("planning.legacy.assignments.new")}
        </Button>
      </div>

      <AssignmentInfoAlert />

      <AssignmentTable
        assignments={assignments}
        employees={employees}
        schedules={schedules}
        cyclePatterns={cyclePatterns}
        handleOpenEditModal={handleOpenEditModal}
        handleOpenDeleteDialog={handleOpenDeleteDialog}
      />

      <AssignmentCreateDialog
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        formData={formData}
        setFormData={setFormData}
        employees={employees}
        schedules={schedules}
        cyclePatterns={cyclePatterns}
        handleCreateAssignment={handleCreateAssignment}
      />

      <AssignmentEditDialog
        open={showEditModal}
        onOpenChange={setShowEditModal}
        formData={formData}
        setFormData={setFormData}
        schedules={schedules}
        cyclePatterns={cyclePatterns}
        handleUpdateAssignment={handleUpdateAssignment}
      />

      <AssignmentDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        handleDeleteAssignment={handleDeleteAssignment}
      />
    </div>
  );
};

export default ScheduleAssignmentManager;
